import { useTranslation } from 'react-i18next';
import { EntityPicker } from '@/shared/ui/components/composed/entity-picker/EntityPicker';
import { backendPropertiesCrud } from '@services/housien';
import { propertyHeadline, propertyCategory, propertyCityState } from '@/shared/utils/property-display';
import { partnershipsPropertyLabel } from '@/shared/utils/partnerships-property-label';

type PropertyRecord = Record<string, unknown>;

interface PropertyPickerProps {
  agencyId: string;
  onSelect: (property: PropertyRecord) => void;
  onClose: () => void;
}

/**
 * Fetches an agency's properties for the picker, unwrapped from the
 * paginated CRUD response.
 *
 * @param agencyId - The agency identifier.
 * @returns The property records.
 */
async function fetchProperties(agencyId: string): Promise<PropertyRecord[]> {
  const result = await backendPropertiesCrud(agencyId).search({ pagination: { page: 1, limit: 50 } });
  return Object.values(result.data || {}) as PropertyRecord[];
}

/**
 * Derives the bedroom count from either the `characteristics` or `housing`
 * sub-record, whichever carries it.
 *
 * @param p - The property record.
 * @returns The bedroom count, or undefined when neither sub-record has it.
 */
function bedroomsOf(p: PropertyRecord): unknown {
  const chars = p.characteristics as Record<string, unknown> | undefined;
  const housing = p.housing as Record<string, unknown> | undefined;
  return chars?.bedrooms ?? housing?.bedrooms;
}

/**
 * Modal picker that lists an agency's properties (with search) and lets the
 * user select one as the partner-search reference.
 *
 * Thin wrapper around the shared `EntityPicker` — mirrors the client picker.
 *
 * @param props - The agency id, selection callback, and close callback.
 * @returns The property selection modal.
 */
export function PropertyPicker({ agencyId, onSelect, onClose }: PropertyPickerProps) {
  const { t } = useTranslation('features/partnerships');

  return (
    <EntityPicker<PropertyRecord>
      agencyId={agencyId}
      onSelect={onSelect}
      onClose={onClose}
      title={t('wizard.pick_property')}
      fetchItems={fetchProperties}
      getKey={(p) => p.id as string}
      matchesSearch={(p, search) => partnershipsPropertyLabel(p).toLowerCase().includes(search.toLowerCase())}
      renderPrimary={(p) => (
        <>
          {propertyHeadline(p) ?? propertyCategory(p) ?? t('wizard.property_fallback')}
          {propertyCityState(p).city ? ` · ${propertyCityState(p).city}` : ''}
        </>
      )}
      renderSecondary={(p) => {
        const beds = bedroomsOf(p);
        return (
          <>
            {((p.location as Record<string, unknown>)?.neighborhood as string) || (p.neighborhood as string) || ''}
            {beds ? ` • ${beds} qts` : ''}
          </>
        );
      }}
    />
  );
}
